import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideStore, Store } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';

import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  facebookAccountFactory,
  facebookAppConfig,
  HttpLoaderFactory,
  initialFactory,
  permissionFactory
} from '@core/config';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { AuthService } from '@core/services/auth.service';
import { provideEffects } from '@ngrx/effects';
import { AuthEffects } from '@state/auth/auth.effects';
import { authReducer } from '@state/auth/auth.reducers';
import { systemReducer } from '@state/system/system.reducers';
import { taskReducer } from '@state/task/task.reducers';
import { routes } from './app.routes';
import { FacebookAdsAdminService } from '@core/services/facebook.service.ts/facebook-ads-admin.service';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { NgxEditorModule } from 'ngx-editor';
import { MarkdownModule, MARKED_OPTIONS, MERMAID_OPTIONS } from 'ngx-markdown';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { environment } from 'src/environments/environment';
import { provideMessaging } from '@angular/fire/messaging';
import { getMessaging } from 'firebase/messaging';

export const provideTranslation = () => ({
  defaultLanguage: 'en',
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient]
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideStore({
      auth: authReducer,
      system: systemReducer,
      task: taskReducer
    }),
    provideEffects(AuthEffects),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initialFactory,
      deps: [Store, AuthService, PrimeNGConfig]
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: permissionFactory,
      deps: [Store, AuthService, NgxPermissionsService]
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: facebookAppConfig,
      deps: [Store, AuthService, FacebookAdsAdminService]
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: facebookAccountFactory,
      deps: [Store, AuthService, FacebookAdsAdminService]
    },
    importProvidersFrom([
      TranslateModule.forRoot(provideTranslation()),
      MonacoEditorModule.forRoot(),
      MarkdownModule.forRoot({
          markedOptions: {
            provide: MARKED_OPTIONS,
            useFactory: () => {
              return {
                gfm: true,
                breaks: false,
                pedantic: false,
                highlight: (code: string) => code
              };
            },
          },
          mermaidOptions: {
            provide: MERMAID_OPTIONS,
            useValue: {
              darkMode: true,
              look: 'handDrawn',
            },
          },
        }),
    ]),
    provideFirebaseApp(() => initializeApp(environment.FIREBASE_CONFIG)),
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    MessageService
  ]
};
